// breather/core/Sequencer.ts
import gsap from 'gsap'

export type PhaseDef = {
  id: string
  label?: string
  copy?: string
  duration: number
  // Legacy fields — no longer read by main.tsx, kept optional for backwards compatibility
  color?: string
  lightY?: number
  pressure?: number
  elongate?: number
}

export type SequenceDef = {
  name: string
  title?: string
  badge?: string
  tagline?: string
  cycles?: number
  phases: PhaseDef[]
}
export type PhaseEvent = 'phaseStart' | 'phaseProgress' | 'phaseEnd' | 'cycleEnd' | 'sessionEnd'

export type SequencerOptions = {
  cyclesOverride?: number
  guidedMode?: boolean
  showPulse?: boolean
}

export class Sequencer {
  private seq: SequenceDef
  private cycles: number
  private listeners: Record<PhaseEvent, ((ctx: any) => void)[]> = {
    phaseStart: [],
    phaseProgress: [],
    phaseEnd: [],
    cycleEnd: [],
    sessionEnd: []
  }
  private isRunning = false
  private guidedMode = false
  private showPulse = false

  constructor(seq: SequenceDef, opts?: SequencerOptions) {
    this.seq = seq
    this.cycles = opts?.cyclesOverride ?? seq.cycles ?? 4
    this.guidedMode = opts?.guidedMode ?? false
    this.showPulse = opts?.showPulse ?? false
  }

  on(evt: PhaseEvent, cb: (ctx: any) => void) {
    this.listeners[evt].push(cb)
    return this
  }

  private emit(evt: PhaseEvent, ctx: any) {
    this.listeners[evt].forEach(f => f(ctx))
  }

  start() {
    if (this.isRunning) return
    this.isRunning = true
    this.runCycle(1)
  }

  private async runCycle(cycle: number) {
    for (const phase of this.seq.phases) {
      this.emit('phaseStart', { cycle, phase })

      let remaining = phase.duration
      const ticker = () => this.emit('phaseProgress', { cycle, phase, remaining })
      gsap.ticker.add(ticker)

      await new Promise<void>(resolve => {
        gsap.to({}, {
          duration: phase.duration,
          ease: 'none',
          onUpdate() { remaining = (1 - this.progress()) * phase.duration },
          onComplete: resolve
        })
      })

      gsap.ticker.remove(ticker)
      this.emit('phaseEnd', { cycle, phase })
    }

    this.emit('cycleEnd', { cycle })
    if (cycle < this.cycles) this.runCycle(cycle + 1)
    else { this.isRunning = false; this.emit('sessionEnd', {}) }
  }
}
